import { createInstance, dateReviver } from '@/api';
import {
    CreateProductEntityDataMappingDto,
    ProductEntityDataMappingClient,
    ProductEntityDataMappingDetailDto,
    ProductEntityDetailDto,
    UpdateProductEntityDataMappingDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useProductProcessDefinitionEditStore } from '../product-process-definition/product-process-definition-edit.store';
import { copyProductEntityValues } from '../../common';

export interface ProductEntityDataMappingStoreState {
    items?: { key: string; value: ProductEntityDataMappingDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new ProductEntityDataMappingClient('', createInstance(), dateReviver);

export const useProductEntityDataMappingStore = defineStore('configuration-product-entity-data-mapping-store', {
    state: (): ProductEntityDataMappingStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        byDefinition: (state) => {
            return (definitionId: number): ProductEntityDataMappingDetailDto[] => {
                const key = `definition-${definitionId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        updateProductEntityUI(updatedProductEntity: ProductEntityDetailDto) {
            this.items?.forEach((item) => {
                item.value?.forEach((entity) => {
                    copyProductEntityValues(updatedProductEntity, entity.fromEntity);
                    copyProductEntityValues(updatedProductEntity, entity.toEntity);
                });
            });
        },
        async getByDefinition(
            definitionId: number,
            reloading: boolean = false
        ): Promise<ProductEntityDataMappingDetailDto[]> {
            reloading ? (this.reloading = true) : (this.loading = true);
            const key = `definition-${definitionId}`;

            try {
                const response = await client.getByDefinition(definitionId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
                return response;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('productEntityDataMapping.detail.error.get.title'),
                    error,
                    false
                );
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
            return [];
        },
        async create(definitionId: number, dto: CreateProductEntityDataMappingDto) {
            let hasError = false;
            this.reloading = true;
            try {
                const response = await client.create(dto);
                useAlertStore().setSuccess(i18n.global.t('productEntityDataMapping.detail.success.create.title'));
                this.getByDefinition(definitionId, true);
                return response.id;
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(
                    i18n.global.t('productEntityDataMapping.detail.error.create.title'),
                    error
                );
            } finally {
                this.reloading = false;
                if(!hasError){
                    useProductProcessDefinitionEditStore().closeCreate();
                }
            }
        },
        async update(definitionId: number, transformationId: number, dto: UpdateProductEntityDataMappingDto) {
            let hasError = false;
            this.reloading = true;
            try {
                await client.update(transformationId, dto);
                useAlertStore().setSuccess(i18n.global.t('productEntityDataMapping.detail.success.update.title'));
                this.getByDefinition(definitionId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(
                    i18n.global.t('productEntityDataMapping.detail.error.update.title'),
                    error
                );
            } finally {
                this.reloading = false;
                if(!hasError){
                    useProductProcessDefinitionEditStore().closeEdit();
                }
            }
        },
        async remove(definitionId: number, id: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('productEntityDataMapping.detail.success.delete.title'));
                await this.getByDefinition(definitionId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('productEntityDataMapping.detail.error.delete.title'),
                    error
                );
            } finally {
                this.reloading = false;
            }
            return false;
        },
        async reset(definitionId: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.clearByDefinitionId(definitionId);
                useAlertStore().setSuccess(i18n.global.t('productEntityDataMapping.detail.success.reset.title'));
                await this.getByDefinition(definitionId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('productEntityDataMapping.detail.error.reset.title'),
                    error
                );
            } finally {
                this.reloading = false;
            }
            return false;
        },
        async generate(definitionId: number) {
            this.reloading = true;
            try {
                await client.generateByDefinitionId(definitionId);
                useAlertStore().setSuccess(i18n.global.t('productEntityDataMapping.detail.success.generate.title'));
                this.getByDefinition(definitionId, true);
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('productEntityDataMapping.detail.error.generate.title'),
                    error
                );
            } finally {
                this.reloading = false;
                useProductProcessDefinitionEditStore().closeCreate();
            }
        },
        clear(definitionId: number) {
            const key = `definition-${definitionId}`;

            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
